import { carData as originalCarData } from '../data/car.data';
import { colorsData as originalColorsData } from '../data/colors.data';
import { sceneData as originalSceneData } from '../data/scenes.data';
import { pricesData as originalPricesData } from '../data/prices.data';
import { cotizaData as originalCotizaData } from '../data/cotiza.data';
import { videoData as originalVideoData } from '../data/video.data';
import { fichasData as originalFichasData } from '../data/fichas.data';
import { sliderData as originalSliderData } from '../data/slider.data';
import { billboardsData as originalBillboardsData } from '../data/billboards.data';
import { carMenu as originalCarMenu } from '../data/mobileCarMenu.data';
import { carMenuInterior as originalCarMenuInterior } from '../data/mobileCarMenuInterior.data';

// Define empty fallbacks since web builds fetch directly from API
const cacheConfig: any = {};
const cacheCars: any = {};
const cachePrices: any = {};
const cacheCotiza: any = {};
const cacheVideos: any = {};
const cacheFichas: any = {};
const cacheColors: any = {};
const cacheScenes: any = {};
const cacheSlider: any = {};
const cacheBillboards: any = {};

function getStrapiUrl(): string {
  if (process.env.REACT_APP_STRAPI_URL) {
    return process.env.REACT_APP_STRAPI_URL;
  }
  if (typeof window !== 'undefined' && window.location) {
    // Permitir forzar producción localmente con un parámetro en la URL: ?prod=true o ?strapi=prod
    const params = new URLSearchParams(window.location.search);
    if (params.get('prod') === 'true' || params.get('strapi') === 'prod') {
      return 'https://admin-hics.umbraint.com';
    }

    const hostname = window.location.hostname;
    // Si no estamos en localhost/IPs locales, y no es protocolo local 'file:', asumimos entorno producción web
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.');
    const isFileProtocol = window.location.protocol === 'file:';
    if (!isLocal && !isFileProtocol) {
      return 'https://admin-hics.umbraint.com';
    }
  }
  return 'https://admin-hics.umbraint.com';
}

const STRAPI_URL = getStrapiUrl();

export function resolveAssetUrl(urlPath: any): any {
  if (!urlPath) return urlPath;
  if (typeof urlPath === 'string') {
    if (urlPath.startsWith('/uploads/')) {
      return `${STRAPI_URL}${urlPath}`;
    }
  }
  return urlPath;
}

// Variable de control para habilitar/deshabilitar la sincronización con Strapi
const ENABLE_STRAPI_SYNC = false;

export async function syncStrapiRuntime(): Promise<boolean> {
  if (!ENABLE_STRAPI_SYNC) {
    console.log('Sincronización con Strapi desactivada mediante variable de control (ENABLE_STRAPI_SYNC).');
    return false;
  }
  try {
    console.log('Iniciando sincronización dinámica con Strapi...');

    // 1. Configuración General
    let configGlobal: any = null;
    try {
      const configRes = await fetch(`${STRAPI_URL}/api/configuracion-general?populate=*&_t=${Date.now()}`, { cache: 'no-store' });
      if (configRes.ok) {
        const configJson = await configRes.json();
        configGlobal = configJson.data;
      }
    } catch (err) {
      console.warn('Aviso: No se pudo obtener la configuración general desde Strapi.');
    }

    // 2. Vehículos
    let vehiculos: any[] = [];
    try {
      const queryParams = [
        'populate[colores][populate]=*',
        'populate[escenarios][populate]=*',
        'populate[hotspots_exteriores][populate]=*',
        'populate[hotspots_interiores][populate]=*',
        'populate[preview_slider][populate]=*',
        'populate[info_slider][populate]=*',
        'sort[0]=orden:asc'
      ].join('&');
      const vehiculosRes = await fetch(`${STRAPI_URL}/api/vehiculos?${queryParams}&_t=${Date.now()}`, { cache: 'no-store' });
      if (vehiculosRes.ok) {
        const vehiculosJson = await vehiculosRes.json();
        vehiculos = vehiculosJson.data || [];
      }
    } catch (err) {
      console.warn('Aviso: No se pudieron obtener los vehículos desde Strapi.');
    }

    if (!configGlobal && vehiculos.length === 0) {
      console.log('No se obtuvieron datos nuevos de Strapi. Se usará la caché existente o los datos estáticos.');
      return false;
    }

    // Estructuras de caché modular
    const runtimeConfig = {
      encender_vehiculo: configGlobal ? configGlobal.encender_vehiculo : true,
      sonido_activo: configGlobal ? configGlobal.sonido_activo : true,
      texto_legal: configGlobal ? configGlobal.texto_legal : 'Las imágenes de los vehículos son de carácter ilustrativo...',
      musica_ambiente: configGlobal?.musica_ambiente?.url || ''
    };

    const runtimeCars: any = {};
    const runtimePrices: any = {};
    const runtimeCotiza: any = {};
    const runtimeVideos: any = {};
    const runtimeFichas: any = {};
    const runtimeColors: any = {};
    const runtimeScenes: any = {};
    const runtimeSlider: any = {};
    const runtimeBillboards: any = {};

    for (const vehiculo of vehiculos) {
      if (!vehiculo.activo) continue;

      const modelKey = vehiculo.id_modelo;
      const versionKey = vehiculo.version_default;
      const editionKey = `${modelKey} ${versionKey}`;

      if (!runtimeCars[modelKey]) runtimeCars[modelKey] = {};
      runtimeCars[modelKey][versionKey] = {
        showViews: vehiculo.mostrar_sillas ?? true,
        nombre_lateral: vehiculo.nombre_lateral || '',
        version_visible: vehiculo.version_visible || ''
      };

      runtimeSlider[editionKey] = {
        name: modelKey,
        nameTitle: vehiculo.nameTitle || '',
        nameAlt: vehiculo.nameAlt || '',
        bgColor: vehiculo.color_fondo_slider || '',
        preview: vehiculo.preview_slider?.url || '',
        info: vehiculo.info_slider?.url || '',
        orden: vehiculo.orden ?? 0
      };

      runtimePrices[editionKey] = vehiculo.precio || '';
      runtimeCotiza[editionKey] = vehiculo.url_cotizacion || '';
      runtimeVideos[editionKey] = vehiculo.url_video_testimonial || '';
      runtimeFichas[editionKey] = vehiculo.pdf_ficha_tecnica || '';

      runtimeColors[editionKey] = {};
      if (vehiculo.colores) {
        for (const color of vehiculo.colores) {
          const colorKey = color.id_color;
          runtimeColors[editionKey][colorKey] = {
            name: color.nombre_visible,
            activo: color.activo !== false,
            Image: color.imagen_circulo?.url || ''
          };
        }
      }

      runtimeBillboards[editionKey] = { exterior: [], interior: [] };
      const processHotspots = (hotspots: any[], type: 'exterior' | 'interior') => {
        if (!hotspots) return;
        hotspots.forEach((h: any) => {
          runtimeBillboards[editionKey][type].push({
            title: h.titulo,
            text: h.descripcion || '',
            position: h.coordenadas ? { x: h.coordenadas.x, y: h.coordenadas.y, z: h.coordenadas.z } : null,
            activo: h.activo !== false,
            image: h.imagen?.url || ''
          });
        });
      };

      processHotspots(vehiculo.hotspots_exteriores, 'exterior');
      processHotspots(vehiculo.hotspots_interiores, 'interior');

      // Escenarios mapeados por vehículo
      runtimeScenes[editionKey] = [];
      if (vehiculo.escenarios) {
        for (const escenario of vehiculo.escenarios) {
          runtimeScenes[editionKey].push({
            id_escenario: escenario.id_escenario,
            name: escenario.nombre,
            default: escenario.es_predeterminado || false,
            activo: escenario.activo !== false,
            icon: escenario.icono?.url || ''
          });
        }
      }
    }

    // Guardar en LocalStorage
    if (configGlobal) {
      localStorage.setItem('strapi_cache_config', JSON.stringify(runtimeConfig));
    }
    if (vehiculos.length > 0) {
      localStorage.setItem('strapi_cache_cars', JSON.stringify(runtimeCars));
      localStorage.setItem('strapi_cache_prices', JSON.stringify(runtimePrices));
      localStorage.setItem('strapi_cache_cotiza', JSON.stringify(runtimeCotiza));
      localStorage.setItem('strapi_cache_videos', JSON.stringify(runtimeVideos));
      localStorage.setItem('strapi_cache_fichas', JSON.stringify(runtimeFichas));
      localStorage.setItem('strapi_cache_colors', JSON.stringify(runtimeColors));
      localStorage.setItem('strapi_cache_scenes', JSON.stringify(runtimeScenes));
      localStorage.setItem('strapi_cache_slider', JSON.stringify(runtimeSlider));
      localStorage.setItem('strapi_cache_billboards', JSON.stringify(runtimeBillboards));
    }

    console.log('¡Sincronización dinámica por caché en tiempo de ejecución completada con éxito!');
    return true;
  } catch (error) {
    console.error('Error durante la sincronización dinámica:', error);
    return false;
  }
}

function getCachedData(key: string, fallback: any) {
  if (!ENABLE_STRAPI_SYNC) {
    return fallback;
  }
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem(key);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch (e) {
        console.error('Error parsing localStorage key ' + key, e);
      }
    }
  }
  return fallback;
}

function findCacheKey(cache: any, baseKey: string): string {
  if (!cache || typeof cache !== 'object') return '';
  if (cache[baseKey] !== undefined) return baseKey;

  const keys = Object.keys(cache);

  // Exact match case insensitive
  let matched = keys.find(k => k.toLowerCase() === baseKey.toLowerCase());
  if (matched) return matched;

  // Search by name attribute or prefix
  matched = keys.find(k => {
    const item = cache[k];
    if (item && typeof item === 'object' && item.name) {
      if (String(item.name).toLowerCase() === baseKey.toLowerCase()) return true;
    }
    const kWords = k.toLowerCase().split(/\s+/);
    const baseWords = baseKey.toLowerCase().split(/\s+/);
    if (kWords[0] !== baseWords[0]) return false;
    return kWords.every((w, i) => baseWords[i] === w) || baseWords.every((w, i) => kWords[i] === w);
  });

  return matched || '';
}

export function getConfigData(): any {
  const cache = getCachedData('strapi_cache_config', cacheConfig);
  const resolvedMusic = cache?.musica_ambiente ? resolveAssetUrl(cache.musica_ambiente) : '';
  return {
    encender_vehiculo: cache?.encender_vehiculo ?? true,
    sonido_activo: cache?.sonido_activo ?? true,
    texto_legal: cache?.texto_legal || 'Las imágenes de los vehículos son de carácter ilustrativo, las versiones comercializadas en Colombia pueden variar en colores y accesorios según las distintas versiones.',
    musica_ambiente: resolvedMusic
  };
}

export function getCarData(): any {
  const cache = getCachedData('strapi_cache_cars', cacheCars) as any;
  const base = originalCarData() as any;
  const merged = { ...base } as any;
  const hasCache = cache && Object.keys(cache).length > 0;

  Object.keys(base).forEach(modelKey => {
    if (merged[modelKey]) {
      Object.keys(merged[modelKey]).forEach(versionKey => {
        const cacheVer = cache[modelKey]?.[versionKey];
        if (cacheVer) {
          merged[modelKey][versionKey] = {
            ...merged[modelKey][versionKey],
            ...cacheVer
          };
        } else {
          if (hasCache) {
            delete merged[modelKey][versionKey];
          }
        }
      });
      if (Object.keys(merged[modelKey]).length === 0) {
        delete merged[modelKey];
      }
    }
  });
  return merged;
}

export function getPricesData(): any {
  const cache = getCachedData('strapi_cache_prices', cachePrices) as any;
  const base = originalPricesData() as any;
  const merged = { ...base } as any;
  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    if (cacheKey && cache[cacheKey] !== undefined) {
      merged[editionKey] = cache[cacheKey];
    }
  });
  return merged;
}

export function getCotizaData(): any {
  const cache = getCachedData('strapi_cache_cotiza', cacheCotiza) as any;
  const base = originalCotizaData() as any;
  const merged = { ...base } as any;
  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    if (cacheKey && cache[cacheKey] !== undefined) {
      merged[editionKey] = cache[cacheKey];
    }
  });
  return merged;
}

export function getVideosData(): any {
  const cache = getCachedData('strapi_cache_videos', cacheVideos) as any;
  const base = originalVideoData() as any;
  const merged = { ...base } as any;
  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    if (cacheKey && cache[cacheKey] !== undefined) {
      merged[editionKey] = cache[cacheKey];
    }
  });
  return merged;
}

export function getFichasData(): any {
  const cache = getCachedData('strapi_cache_fichas', cacheFichas) as any;
  const base = originalFichasData() as any;
  const merged = { ...base } as any;
  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    if (cacheKey && cache[cacheKey] !== undefined) {
      merged[editionKey] = resolveAssetUrl(cache[cacheKey]);
    } else {
      merged[editionKey] = resolveAssetUrl(merged[editionKey]);
    }
  });
  return merged;
}

export function getSliderData(): any {
  const cache = getCachedData('strapi_cache_slider', cacheSlider) as any;
  const base = originalSliderData() as any;
  const merged = { ...base } as any;
  const matchedCacheKeys = new Set<string>();
  const hasCache = cache && Object.keys(cache).length > 0;

  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    if (cacheKey && cache[cacheKey] !== undefined) {
      matchedCacheKeys.add(cacheKey);
      const cacheEd = cache[cacheKey];
      merged[editionKey] = {
        ...merged[editionKey],
        ...cacheEd,
        preview: cacheEd.preview ? resolveAssetUrl(cacheEd.preview) : resolveAssetUrl(merged[editionKey]?.preview),
        info: cacheEd.info ? resolveAssetUrl(cacheEd.info) : resolveAssetUrl(merged[editionKey]?.info)
      };
    } else {
      if (hasCache) {
        delete merged[editionKey];
      } else {
        if (merged[editionKey]) {
          merged[editionKey] = {
            ...merged[editionKey],
            preview: resolveAssetUrl(merged[editionKey].preview),
            info: resolveAssetUrl(merged[editionKey].info)
          };
        }
      }
    }
  });
  // Include vehicles from Strapi that don't exist in the static base
  if (cache && typeof cache === 'object') {
    Object.keys(cache).forEach(cacheKey => {
      if (!matchedCacheKeys.has(cacheKey)) {
        const cacheEd = cache[cacheKey];
        merged[cacheKey] = {
          ...cacheEd,
          preview: cacheEd.preview ? resolveAssetUrl(cacheEd.preview) : '',
          info: cacheEd.info ? resolveAssetUrl(cacheEd.info) : ''
        };
      }
    });
  }
  // Filter out excluded vehicles
  const excludedKeywords = ['CRETA', 'PALISADE', 'STARIA'];
  Object.keys(merged).forEach(key => {
    if (excludedKeywords.some(keyword => key.toUpperCase().includes(keyword))) {
      delete merged[key];
    }
  });

  return merged;
}

export function getColorsData(): any {
  const cache = getCachedData('strapi_cache_colors', cacheColors) as any;
  const base = originalColorsData() as any;
  const merged = { ...base } as any;

  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    const cacheVer = cacheKey ? cache[cacheKey] : {};
    const hasCacheColors = cacheVer && Object.keys(cacheVer).length > 0;
    if (merged[editionKey]) {
      if (hasCacheColors) {
        const orderedColors: any = {};
        Object.keys(cacheVer).forEach(colorKey => {
          const cacheCol = cacheVer[colorKey];
          if (cacheCol && cacheCol.activo !== false) {
            const baseCol = base[editionKey][colorKey] || {};
            orderedColors[colorKey] = {
              ...baseCol,
              name: cacheCol.name || baseCol.name || colorKey,
              Image: cacheCol.Image ? resolveAssetUrl(cacheCol.Image) : (baseCol.Image ? resolveAssetUrl(baseCol.Image) : '')
            };
          }
        });
        merged[editionKey] = orderedColors;
      } else {
        merged[editionKey] = { ...merged[editionKey] };
        Object.keys(merged[editionKey]).forEach(colorKey => {
          merged[editionKey][colorKey] = {
            ...merged[editionKey][colorKey],
            Image: resolveAssetUrl(merged[editionKey][colorKey].Image)
          };
        });
      }
    }
  });
  return merged;
}

export function getSceneData(editionKey?: string): any {
  const cache = getCachedData('strapi_cache_scenes', cacheScenes) as any;
  const base = originalSceneData() as any;

  if (editionKey) {
    const cacheKey = findCacheKey(cache, editionKey);
    const vehicleScenes = cacheKey ? cache[cacheKey] : null;

    if (vehicleScenes && Array.isArray(vehicleScenes)) {
      const merged: any = {};
      vehicleScenes.forEach((esc: any) => {
        if (esc.activo === false) return;
        const escKey = esc.id_escenario;
        const baseScene = base[escKey] || {};
        const resolvedIcon = esc.icon ? resolveAssetUrl(esc.icon) : resolveAssetUrl(baseScene.icon);
        merged[escKey] = {
          ...baseScene,
          name: esc.name || baseScene.name || escKey,
          default: esc.default ?? baseScene.default ?? false,
          icon: typeof resolvedIcon === 'string' ? { default: resolvedIcon } : resolvedIcon
        };
      });
      // Asegurarse de tener al menos la escena de Estudio por defecto
      if (Object.keys(merged).length === 0 && base.sinfin) {
        merged.sinfin = base.sinfin;
      }
      return merged;
    }
  }

  // Fallback si no se pasa editionKey o el cache está vacío para este vehículo
  const merged = { ...base } as any;

  if (cache && typeof cache === 'object' && !Array.isArray(cache)) {
    const keys = Object.keys(cache);
    const isOldFormat = keys.length > 0 && !Array.isArray(cache[keys[0]]);

    if (isOldFormat) {
      keys.forEach(sceneKey => {
        const cacheSc = cache[sceneKey];
        if (cacheSc) {
          if (cacheSc.activo === false) {
            delete merged[sceneKey];
          } else {
            const resolvedIcon = cacheSc.icon ? resolveAssetUrl(cacheSc.icon) : resolveAssetUrl(merged[sceneKey]?.icon);
            merged[sceneKey] = {
              ...merged[sceneKey],
              name: cacheSc.name || merged[sceneKey]?.name,
              default: cacheSc.default ?? merged[sceneKey]?.default,
              icon: typeof resolvedIcon === 'string' ? { default: resolvedIcon } : resolvedIcon
            };
          }
        }
      });
      return merged;
    }
  }

  const activeKeys = new Set<string>();
  let hasCache = false;

  Object.keys(cache).forEach(key => {
    const item = cache[key];
    if (Array.isArray(item)) {
      hasCache = true;
      item.forEach((esc: any) => {
        if (esc.activo !== false) {
          activeKeys.add(esc.id_escenario);
        }
      });
    }
  });

  if (hasCache) {
    Object.keys(base).forEach(sceneKey => {
      if (!activeKeys.has(sceneKey)) {
        delete merged[sceneKey];
      }
    });
  }

  Object.keys(cache).forEach(key => {
    const item = cache[key];
    if (Array.isArray(item)) {
      item.forEach((esc: any) => {
        const escKey = esc.id_escenario;
        if (merged[escKey]) {
          const resolvedIcon = esc.icon ? resolveAssetUrl(esc.icon) : resolveAssetUrl(merged[escKey].icon);
          merged[escKey] = {
            ...merged[escKey],
            name: esc.name || merged[escKey].name,
            icon: typeof resolvedIcon === 'string' ? { default: resolvedIcon } : resolvedIcon
          };
        }
      });
    }
  });

  return merged;
}

export function getBillboardsData(): any {
  const cache = getCachedData('strapi_cache_billboards', cacheBillboards) as any;
  const base = originalBillboardsData() as any;
  const merged = { ...base } as any;

  Object.keys(base).forEach(editionKey => {
    const cacheKey = findCacheKey(cache, editionKey);
    const cacheEd = cacheKey ? cache[cacheKey] : {};
    if (merged[editionKey]) {
      merged[editionKey] = {
        exterior: (base[editionKey].exterior || []).map((orig: any, idx: number) => {
          let cacheItem = (cacheEd.exterior || []).find(
            (item: any) => String(item.title).toLowerCase().trim() === String(orig.title).toLowerCase().trim()
          );
          if (!cacheItem && cacheEd.exterior && cacheEd.exterior[idx]) {
            cacheItem = cacheEd.exterior[idx];
          }

          if (cacheItem) {
            return {
              ...orig,
              ...cacheItem,
              position: cacheItem.position || orig.position,
              image: cacheItem.image ? resolveAssetUrl(cacheItem.image) : resolveAssetUrl(orig.image)
            };
          }
          return {
            ...orig,
            image: resolveAssetUrl(orig.image)
          };
        }).filter((item: any) => item.activo !== false),

        interior: (base[editionKey].interior || []).map((orig: any, idx: number) => {
          let cacheItem = (cacheEd.interior || []).find(
            (item: any) => String(item.title).toLowerCase().trim() === String(orig.title).toLowerCase().trim()
          );
          if (!cacheItem && cacheEd.interior && cacheEd.interior[idx]) {
            cacheItem = cacheEd.interior[idx];
          }

          if (cacheItem) {
            return {
              ...orig,
              ...cacheItem,
              position: cacheItem.position || orig.position,
              image: cacheItem.image ? resolveAssetUrl(cacheItem.image) : resolveAssetUrl(orig.image)
            };
          }
          return {
            ...orig,
            image: resolveAssetUrl(orig.image)
          };
        }).filter((item: any) => item.activo !== false)
      };
    }
  });
  return merged;
}

export function getCarMenu(): any {
  const base = originalCarMenu() as any;
  const config = getConfigData();
  const merged = { ...base } as any;

  Object.keys(base).forEach(key => {
    const menuObj = base[key] || {};
    let menuItems = menuObj.menu || [];
    if (!config.encender_vehiculo) {
      menuItems = menuItems.filter((item: any) => item.slug !== 'luces');
    }
    merged[key] = { menu: menuItems };
  });
  return merged;
}

export function getCarMenuInterior(): any {
  const base = originalCarMenuInterior() as any;
  const config = getConfigData();
  const merged = { ...base } as any;

  Object.keys(base).forEach(key => {
    const menuObj = base[key] || {};
    let menuItems = menuObj.menu || [];
    if (!config.encender_vehiculo) {
      menuItems = menuItems.filter((item: any) => item.slug !== 'luces-interior');
    }
    merged[key] = { menu: menuItems };
  });
  return merged;
}

export function getCarDisplayName(modelKey: string, versionKey: string): string {
  const carData = getCarData() as any;
  const modelVersion = carData[modelKey]?.[versionKey] || {};
  if (modelVersion.nombre_lateral) {
    return modelVersion.nombre_lateral;
  }
  const slider = getSliderData() as any;
  const editionKey = `${modelKey} ${versionKey}`;
  if (slider[editionKey]?.nameTitle) {
    return slider[editionKey].nameTitle;
  }
  return modelKey;
}

function formatSingleWord(word: string): string {
  const upper = word.toUpperCase();
  if (upper === 'AT' || upper === 'HEV' || upper === 'EV' || upper === '4X4') {
    return upper === '4X4' ? '4x4' : upper;
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function formatVersionName(name: string): string {
  if (!name) return name;
  const words = name.split(' ');
  const formattedWords = words.map((word) => {
    if (word.includes('-')) {
      return word.split('-').map(subWord => formatSingleWord(subWord)).join('-');
    }
    return formatSingleWord(word);
  });
  return formattedWords.join(' ');
}

export function getVersionDisplayName(modelKey: string, versionKey: string): string {
  const carData = getCarData() as any;
  const modelVersion = carData[modelKey]?.[versionKey] || {};
  const rawVersionName = modelVersion.version_visible || versionKey;
  return formatVersionName(rawVersionName);
}
